// import { ConsoleLogger } from "aws-amplify/utils";
import React, { createContext, useContext, useState, useRef, useEffect } from "react";
import { getCurrentUser, fetchUserAttributes} from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { Amplify } from 'aws-amplify';
import outputs from '../../amplifyconfiguration.json';

import { createDevelopmentPlan } from '../../graphql/mutations';
import { getUser } from '../../graphql/queries';
import { v4 as uuidv4 } from 'uuid';

// Set up for accessing the GraphQL API (connecs user info to development plans)
Amplify.configure(outputs);
const client = generateClient({
    authMode: "apiKey",
    apiKey: outputs.aws_appsync_apiKey,
    region: outputs.aws_appsync_region,
    url: outputs.aws_appsync_graphqlEndpoint,
});

const MapContext = createContext();

export const MapProvider = ({ children }) => {

    ///// USER DATA /////

    const [userData, setUserData] = useState(null);
    const [userEmail, setUserEmail] = useState(null); 

    // Define GraphQL operations
    const getUserQuery = `
    query GetUser($id: ID!) { getUser(id: $id) {
            id
            email
            developmentPlans {
                items {
                    id
                    name
                    version
                }
            }
        }
    }`;

    const createUserMutation = `
    mutation CreateUser($input: CreateUserInput!) {
        createUser(input: $input) {
            id
            email
            developmentPlans {
                items {
                    id
                    name
                    version
                }
            }
        }
    }`;

    ///// MAP DATA /////

    const [geoJSONData, setGeoJSONData] = useState({
        subcatchments: null, // State for subcatchments GeoJSON data
        outfalls: null,      // State for outfalls GeoJSON data
        overflowData: null,  // State for outfalls overflow data
    });

    const [mapClickInfo, setMapClickInfo] = useState({
        selectedLayer: null, // Whether the map click is on outfalls or subcatchments layer
        assetName: null,     // The name of the map click selected feature
    })

    const [selectedFeature, setSelectedFeature] = useState({ 
        ID: null,   // The ID of the map click selected feature
        Data: null  // The model data for the map click selected feature
    })

    const mapRef = useRef(null);

    ///// TIMESERIES DATA /////

    const [timeSeries, setTimeSeries] = useState({
        timeseriesData: null, // State for timesereis runoff / overflow data (API call)
        timeseriesSum: null,  // We take the timeseries data and sum it
        rainfallData: null,   // State for the rainfall data (API call)
        rainfallSum: null,     // We sum the rainfall timeseries
        infiltrationData: null, // State for the infiltration data (API call)
        infiltrationSum: null, // We sum the infiltration timeseries
        newTimeseriesData: null, // State for the results timeseries data (API call)
        newTimeseriesSum: null, // We sum the results timeseries data
        newInfiltrationData: null, // State for the results infiltration data (API call)
        newInfiltrationSum: null, // We sum the results infiltration data
    })

    const [systemTotals, setSystemTotals] = useState({
        flooding: null, // State for the system totals flooding data
        infiltration: null, // State for the system totals infiltration data
        outflow: null, // State for the system totals outflow data
        runoff: null, // State for the system totals runoff data
    })

    const [systemResults, setSystemResults] = useState({
        flooding: null, // State for the system results flooding data
        infiltration: null, // State for the system results infiltration data
        outflow: null, // State for the system results outflow data
        runoff: null, // State for the system results runoff data
    });

    ///// VIEW CONTROL /////

    const [layersVisible, setLayersVisible] = useState(false); // The top left button that controls subcatchment colors
    const [colorScheme, setColorScheme] = useState('standard'); // The color scheme for the subcatchment colors

    const [LIDMode, setLIDMode] = useState(false); // The top left button that opens the devPlan panel 

    const stormOptions = ['mar27', 'May19', 'Jul18', 'Aug16']; // Removed Sep21 option bc it doesn't look good
    const [selectedStormOption, setSelectedStormOption] = useState('mar27'); // Chosen storm date range

    const [loading, setLoading] = useState(false); // I think this is whether the graph is loading data or not - confirm!

    const [LIDType, setLIDType] = React.useState(''); // The type of LID feature selected in the LID panel
    const [LIDContent, setLIDContent] = useState({}); // this should be obsolete now -- but be careful in deleting!

    const [devPlanID, setDevPlanID] = React.useState(10);
    const [devPlanContent, setDevPlanContent] = useState({}); 

    const [inpFile, setInpFile] = useState('');
    const [outFileExists, setOutFileExists] = useState(false);
    const [planHasBeenRun, setPlanHasBeenRun] = useState(false);

    const [aggregatedCounts, setAggregatedCounts] = useState({}); // what is this???
    const [stormMode, setStormMode] = useState(true);  // We are moving towards deleting this -- but be a little careful! no breaking!

    const [didICreateADevPlan, setDidICreateADevPlan] = useState(false);

    const [floodingVisual, setFloodingVisual] = useState(false); // Visualize flooding in nodes


    ///// USE EFFECTS /////

    // Populates the devPlanContent state with the LID content for the selected development plan
    useEffect(() => {

        if (userData && devPlanID) {
            const inpFiles = [ // we need to check the inp files for every possible storm option
                `GLWA_mar27_${userData.id}_${devPlanID}`, 
                `GLWA_May19_${userData.id}_${devPlanID}`, 
                `GLWA_Jul18_${userData.id}_${devPlanID}`, 
                `GLWA_Aug16_${userData.id}_${devPlanID}`
            ];

            const fetchDevPlanContent = async (inpFile) => {
                try {
                    const response = await fetch(`https://2471d2z813.execute-api.us-east-2.amazonaws.com/read-lid/${inpFile}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (!data.message) {
                            return {'data': data, 'exists': true};
                        } else {
                            return {'data': null, 'exists': false};
                        }
                    } else {
                        return {'data': null, 'exists': false};
                    }
                } catch (error) {
                    console.log("Error fetching LID content:", error);
                }
            }

            const checkForDevPlan = async () => {
                let devPlanExists = false;
                for (let i = 0; i < inpFiles.length; i++) {
                    const inpFile = inpFiles[i];
                    const result = await fetchDevPlanContent(inpFile);
                    if (result.exists) {
                        setDevPlanContent(result.data);
                        console.log("Dev plan content:", result.data);
                        devPlanExists = true;
                        break;
                    }
                }
                if (!devPlanExists) {
                    setDevPlanContent({});
                    console.log("Dev plan content:", {});
                }
            }

            checkForDevPlan();

        }
        
    }, [devPlanID, userData, inpFile, selectedStormOption]);

    // this checks if the development plan has been run for the selected storm option
    useEffect(() => {
        const checkOutFileExists = async (storm, user, plan) => {
            try {
                const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${storm}/${user.id}_${plan}/node/049_DRO/total_inflow`;
                const response = await fetch(url);
                if (response.status === 404) {
                    setOutFileExists(false);
                } else if (response.ok) {
                    const data = await response.json();
                    if (data && data.x) {
                        setOutFileExists(true);
                        setPlanHasBeenRun(true);
                    } else {
                        setOutFileExists(false);
                    }
                } else {
                    console.error("Unexpected response status:", response.status);
                }
            } catch (error) {
                setOutFileExists(false);
                console.error("Error checking outFile existence:", error);
            }
        };
        if (userData && devPlanID) {
            checkOutFileExists(selectedStormOption, userData, devPlanID);
        }
    }, [userData, devPlanID, selectedStormOption]);

    // This checks if the development plan has been run for any of the storms
    useEffect(() => {
        const checkPlanHasBeenRun = async (user, plan) => {
            const storms = ['mar27', 'May19', 'Jul18', 'Aug16'];
            let planHasBeenRun = false;
            for (let i = 0; i < storms.length; i++) {
                try {
                    const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${storms[i]}/${user.id}_${plan}/node/049_DRO/total_inflow`;
                    const response = await fetch(url);
                    if (response.ok) {
                        const data = await response.json();
                        if (data && data.x) {
                            planHasBeenRun = true;
                            console.log("PLAN HAS BEEN RUN FOR:", storms[i]);
                            break;
                        }
                    }
                } catch (error) {
                    console.error("Error checking plan has been run:", error);
                }
            }
            console.log("PLAN HAS BEEN RUN:", planHasBeenRun);
            setPlanHasBeenRun(planHasBeenRun);
        }
        if (userData && devPlanID) {
            checkPlanHasBeenRun(userData, devPlanID);
        }
    }, [userData, devPlanID]);

    // Populates the userData state with the user's ID based on the login email
    // and creates a new user in the GraphQL database if this is the first login
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { username: userId } = await getCurrentUser();
                const attributes = await fetchUserAttributes();
                const email = attributes.email;
                setUserEmail(email);
    
                try {
                    const { data } = await client.graphql({
                        query: getUserQuery,
                        variables: { id: userId },
                        authMode: 'apiKey' 
                    });
    
                    if (data.getUser === null) { // creates a new user in the GraphQL database if one doesn't exist
                        try {
                            const newUserData = await client.graphql({
                                query: createUserMutation,
                                variables: { input: { id: userId, email: email } },
                                authMode: 'apiKey'  // And here
                            });
                            console.log('New user created:', newUserData.data.createUser);
                            setUserData(newUserData.data.createUser);
                            checkAndCreateDevelopmentPlan(newUserData.data.createUser); // this creates a default development plan for the user
                        } catch (mutationError) {
                            console.error('Error creating new user:', mutationError);
                            setUserData({ id: userId, email: email });
                        }
                    } else { setUserData(data.getUser); }
                } catch (graphqlError) {
                    console.error('Detailed GraphQL Error:', {
                        message: graphqlError.message,
                        path: graphqlError?.path,
                        locations: graphqlError?.locations,
                        extensions: graphqlError?.extensions,
                        stack: graphqlError?.stack
                    });
                    setUserData({ id: userId, email: email });
                    console.log('User data set to:', { id: userId, email: email });
                }
            } catch (error) {
                console.error('Error in user authentication:', error);
                setUserData(null);
                setUserEmail(null);
            }
        };
        fetchUserData();
    }, []);

    // Populates the systemTotals state with system-wide baseline data for the selected storm
    useEffect(() => {
        setSystemTotals({ flooding: null, infiltration: null, outflow: null, runoff: null })
        const fetchSystemTotals = async () => {
            try {
                const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/system-results/${selectedStormOption}/og`;
                const response = await fetch(url);
    
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
        
                const data = await response.json();
                setSystemTotals(data);
                console.log('System totals:', data);
            } catch (error) {
                console.error('Error fetching system totals:', error);
                setSystemTotals({ flooding: null, infiltration: null, outflow: null, runoff: null });
            }
        };
        fetchSystemTotals();
    }, [selectedStormOption]);

    // Populates the systemResults state with system-wide results data for the selected storm
    // this only runs if an outFile exists for the specific storm option and development plan selected
    useEffect(() => {
        setSystemResults({ flooding: null, infiltration: null, outflow: null, runoff: null })
        const fetchSystemResults = async () => {
            try {
                const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/system-results/${selectedStormOption}/${userData.id}_${devPlanID}`;
                const response = await fetch(url);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                setSystemResults(data);
                console.log('System results:', data);
            } catch (error) {
                console.error('Error fetching system results:', error);
                setSystemResults({ flooding: null, infiltration: null, outflow: null, runoff: null });
            }
        };

        if (outFileExists && userData && devPlanID) {
            fetchSystemResults();
        }
    }, [outFileExists, devPlanID]);

    // Sets LIDMode to false if layersVisible is true
    useEffect(() => {
        if (layersVisible) {
            setLIDMode(false);
        } 
    }, [layersVisible]);

    // Sets layersVisible to false if LIDMode is true
    useEffect(() => {
        if (LIDMode) {
            setLayersVisible(false);
            setColorScheme('standard');
        }
    }, [LIDMode]);

    // Call fetchTimeseriesData and fetchRainfallData when storm option changes in the OverlayChart.js
    useEffect(() => {
        setLoading(true);
        if (selectedStormOption && selectedFeature.ID && mapClickInfo.assetName) {
            
            setTimeSeries({ timeseriesData: null, timeseriesSum: null, rainfallData: null, rainfallSum: null });
            if (mapClickInfo.selectedLayer === 'subcatchment') {
                fetchTimeseriesData('subcatchment', mapClickInfo.assetName, 'runoff', selectedStormOption);
                fetchTimeseriesData('subcatchment', mapClickInfo.assetName, 'infiltration', selectedStormOption);
                if (outFileExists && userData && devPlanID) {
                    let plan_name = `${userData.id}_${devPlanID}`;
                    fetchTimeseriesData('subcatchment', mapClickInfo.assetName, 'runoff', selectedStormOption, plan_name);
                    fetchTimeseriesData('subcatchment', mapClickInfo.assetName, 'infiltration', selectedStormOption, plan_name);
                }
                fetchRainfallData(mapClickInfo.assetName, selectedStormOption);
                setLoading(false);
            } else if (mapClickInfo.selectedLayer === 'node') {
                fetchTimeseriesData('node', mapClickInfo.assetName, 'total_inflow', selectedStormOption);
                if (outFileExists && userData && devPlanID) {
                    let plan_name = `${userData.id}_${devPlanID}`;
                    fetchTimeseriesData('node', mapClickInfo.assetName, 'total_inflow', selectedStormOption, plan_name);
                }
                setLoading(false);
            }
        }
    }, [selectedStormOption, mapClickInfo, devPlanID, userData, outFileExists]);

    const checkAndCreateDevelopmentPlan = async (newUserData) => {
        console.log("CHECK THE DEVELOPMENT PLANS - DO WE CREATE?")
        if (!newUserData.developmentPlans.items || newUserData.developmentPlans.items.length === 0) {
            try {
                const devPlans = newUserData.developmentPlans?.items;
                if (devPlans && devPlans.length > 0) {
                    console.log('We see userData devPlans:', devPlans);
                } else {
                    console.log('No development plans found');
                    createDefaultDevelopmentPlan(newUserData);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        } else {
            const devPlans = userData.developmentPlans?.items;
            if (devPlans && devPlans.length > 0) {
                console.log('LID Panel sees userData:', devPlans);
            } else { // I don't think this else statement should ever run -- 
                console.log('No development plans found');
                console.log("This else statement shouldn't have run - go find it in the code!")
                createDefaultDevelopmentPlan(newUserData);
            }
        }
    };

    const createDefaultDevelopmentPlan = async (newUserData) => {
        console.log("WERE CREATING A DEV PLAN AND THE USER DATA IS : ", newUserData);
        if (!newUserData.id) {
            console.error('Cannot create development plan: User ID is missing');
            return;
        }
        if (newUserData.developmentPlans && newUserData.developmentPlans.items.length > 0) {
            console.error('Cannot create template development plan: There are already development plans');
            return;
        }
        if (newUserData.developmentPlans && newUserData.developmentPlans.items.some(plan => plan.name === "myFirstDevPlan")) {
            console.error('Cannot create template development plan: myFirstDevPlan already exists');
            return;
        }

        try {
            const newPlan = {
                id: uuidv4(),
                name: "myFirstDevPlan",
                version: 1,
                userID: newUserData.id
            };
            console.log("NEW PLAN IS: ", newPlan);
            const result = await client.graphql({ // this is where the call is made to create plan
                query: createDevelopmentPlan,
                variables: { input: newPlan }
            });
            console.log('Created new development plan:', result.data.createDevelopmentPlan);
            setDevPlanID(result.data.createDevelopmentPlan.id);
            setUserData(prevData => ({ ...prevData, developmentPlans: { items: [...prevData.developmentPlans.items, result.data.createDevelopmentPlan] } }));
        } catch (error) {
            console.error('Error creating development plan:', error);
        }
    };

    // Populates the LIDContent state with the LID content for the selected development plan
    // Do we want to get rid of LIDContent? Check on LIDPanel and then get back to this!!!
    // useEffect(() => {
    //     if (inpFile) {
            
    //         const inpFileWithoutExtension = inpFile.replace('.inp', ''); // Remove the '.inp' extension from the inpFile
    //         const fetchLIDContent = async () => { // Fetch LID content using the file name without extension
    //             try {
    //                 const response = await fetch(`https://2471d2z813.execute-api.us-east-2.amazonaws.com/read-lid/${inpFileWithoutExtension}`);
    //                 if (response.ok) {
    //                     const data = await response.json();
    //                     if (!data.message) {
    //                         setLIDContent(data);
    //                     } else {
    //                         console.log("This inp file hasn't been made yet.");
    //                         setLIDContent({});
    //                     }
    //                 }
    //             } catch (error) {
    //                 console.error("Error fetching LID content:", error);
    //             }
    //         };

    //         const checkOutFileExists = async () => {
    //             try {
    //                 const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${selectedStormOption}/${userData.id}_${devPlanID}/node/049_DRO/total_inflow`;
    //                 const response = await fetch(url);
    //                 if (response.status === 404) {
    //                     setOutFileExists(false);
    //                     console.log("The outFile does not exist (404).");
    //                 } else if (response.ok) {
    //                     const data = await response.json();
    //                     if (data && data.x) {
    //                         setOutFileExists(true);
    //                         console.log("The outFile exists.");
    //                     } else {
    //                         setOutFileExists(false);
    //                         console.log("The outFile does not exist.");
    //                     }
    //                 } else {
    //                     console.error("Unexpected response status:", response.status);
    //                 }
    //             } catch (error) {
    //                 setOutFileExists(false);
    //                 console.error("Error checking outFile existence:", error);
    //             }
    //         };

    //         checkOutFileExists();
    //         fetchLIDContent();
    //     }
    // }, [inpFile]);

    // This is here for the LID panel that is displayed when a model has been run and LID can't be added anymore
    const aggregateLIDCounts = (LIDContent) => {
        const totalCounts = {};
        Object.values(LIDContent).forEach(subcatchment => {
            Object.entries(subcatchment).forEach(([lidType, data]) => {
                const count = parseInt(data.n_replicate, 10);
                if (totalCounts[lidType]) {
                    totalCounts[lidType] += count;
                } else {
                    totalCounts[lidType] = count;
                }
            });
        });
    
        return totalCounts;
    };

    // Makes call to API to get timeseries data for the selected feature
    // variables : either rainfall or runoff
    const fetchTimeseriesData = async (selectedLayer, assetName, variable, selectedStormOption, plan='og') => {
        const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${selectedStormOption}/${plan}/${selectedLayer}/${assetName}/${variable}`;
        console.log('Endpoint (fetchTimeSeriesData):', url);
        const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
        try {
            if (selectedFeature) {
                const response = await fetch(url);
                if (!response.ok) {
                    console.error(`HTTP error! status: ${response.status}`);
                    return;
                }
                const data = await response.json();

                if (data.Error) {
                    console.error(`API error: ${data.Error}`);
                    console.log('Retrying after 1 second...');
                    await delay(1000); // Wait 1 second
                    return fetchTimeseriesData(mapClickInfo.selectedLayer, mapClickInfo.assetName, variable, selectedStormOption, plan);
                }

                if (variable === 'runoff') {
                    if (data.x) {
                        if (plan === 'og') {
                            setTimeSeries(prevData => ({...prevData, timeseriesData: data}));
                            setTimeSeries(prevData => ({
                                ...prevData, 
                                // runoff in cfs, this needs to be multiplied by 900 bc there are 900s in 15 min
                                timeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(3) // MG
                            }));
                        } else {
                            setTimeSeries(prevData => ({...prevData, newTimeseriesData: data}));
                            setTimeSeries(prevData => ({
                                ...prevData, 
                                // runoff in cfs, this needs to be multiplied by 900 bc there are 900s in 15 min
                                newTimeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(3) // MG
                            }));
                        }
                    } else {
                        setTimeSeries(prevData => ({ ...prevData, timeseriesData: {x: [-1], y: [-1]} }));
                        setTimeSeries(prevData => ({...prevData, timeseriesSum: -1 }));
                    }
                } else if (variable === 'infiltration') {
                    if (data.x) {
                        let perv_area = selectedFeature.Data.AREA*43560*(100-selectedFeature.Data.IMPERV)/100
                        if (plan === 'og') {
                            setTimeSeries(prevData => ({...prevData, infiltrationData: data}));
                            setTimeSeries(prevData => ({
                                ...prevData, 
                                // infiltration in in/hr, this needs to be multiplied by area
                                infiltrationSum: (data.y.reduce((a, b) => a + b, 0)*perv_area*0.25*(1/12)*7.48052/1000000).toPrecision(3) // MG
                            }));
                        } else {
                            setTimeSeries(prevData => ({...prevData, newInfiltrationData: data}));
                            setTimeSeries(prevData => ({
                                ...prevData, 
                                // infiltration in in/hr, this needs to be multiplied by area
                                newInfiltrationSum: (data.y.reduce((a, b) => a + b, 0)*perv_area*0.25*(1/12)*7.48052/1000000).toPrecision(3) // MG
                            }));
                        }
                    } else {
                        setTimeSeries(prevData => ({ ...prevData, infiltrationData: {x: [-1], y: [-1]} }));
                        setTimeSeries(prevData => ({ ...prevData, infiltrationSum: -1 }));
                    }
                } else if (variable === 'total_inflow') {
                    if (data.x) {
                        if (plan === 'og') {
                            setTimeSeries(prevData => ({...prevData, timeseriesData: data}));
                            setTimeSeries(prevData => ({
                            ...prevData, 
                            timeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(4) // MG, 900s in 15 min
                            }));
                        } else {
                            setTimeSeries(prevData => ({...prevData, newTimeseriesData: data}));
                            setTimeSeries(prevData => ({
                                ...prevData, 
                                newTimeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(4) // MG, 900s in 15 min
                            }));
                        }
                    } else {
                        setTimeSeries(prevData => ({ ...prevData, timeseriesData: {x: [-1], y: [-1]} }));
                        setTimeSeries(prevData => ({ ...prevData, timeseriesSum: -1 }));
                    }
                }
            }
        } catch (err) {
            console.error('Error loading sample data:', err);
        } 
    };

    // Makes a call to the API to get rainfall data for a given subcatchment
    const fetchRainfallData = async (assetName, selectedStormOption) => {
        const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${selectedStormOption}/og/subcatchment/${assetName}/rainfall`;
        console.log('Endpoint (fetchRainfallData):', url);
        const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
        try {
            const response = await fetch(url);
            const data = await response.json();
            
            if (data.Error) {
                console.error(`API error: ${data.Error}`);
                console.log('Retrying after 1 second...');
                await delay(1000); // Wait 1 second
                return fetchRainfallData(assetName, selectedStormOption);
            }
            if (data.x) {
                const scaledData = {
                    x: data.x,
                    y: data.y.map(value => value * 0.25) // rainfall is given in in/hr, measurements are once every 15 min
                }
                setTimeSeries(prevData => ({ ...prevData, rainfallData: scaledData }));
                setTimeSeries(prevData => ({ ...prevData, rainfallSum: (scaledData.y.reduce((a, b) => a + b, 0)).toPrecision(2) }));
            } else {
                setTimeSeries(prevData => ({ ...prevData, rainfallData: {x: [-1], y: [0]} }));
                setTimeSeries(prevData => ({ ...prevData, rainfallSum: -1 }));
            } 

        } catch (err) {
            console.error('Error loading sample data:', err);
        } 
    };


    // Sets the selected feature ID and data when the map is clicked
    const handleMapClick = (event) => {

        setTimeSeries({ timeseriesData: null, timeseriesSum: null, rainfallData: null, rainfallSum: null });
        const features = mapRef.current.queryRenderedFeatures(event.point, {
            layers: ['subcatchments', 'outfalls']
        });
        
        if (features.length > 0) {
            const clickedFeature = features[0];
            setSelectedFeature(prevFeat => ({ ...prevFeat, ID: clickedFeature.properties.NAME }));
            setMapClickInfo(prevInfo => ({ ...prevInfo, assetName: clickedFeature.properties.NAME }));
            setSelectedFeature(prevFeat => ({ ...prevFeat, Data: clickedFeature.properties}));

            if ('AREA' in clickedFeature.properties) {
                setMapClickInfo(prevInfo => ({ ...prevInfo, selectedLayer: 'subcatchment' }));
                if (selectedStormOption && selectedFeature.ID && mapClickInfo.assetName) {
                    fetchTimeseriesData('subcatchment', clickedFeature.properties.NAME, 'runoff', selectedStormOption);
                    fetchTimeseriesData('subcatchment', clickedFeature.properties.NAME, 'infiltration', selectedStormOption);
                    fetchRainfallData(clickedFeature.properties.NAME, selectedStormOption);
                    if (outFileExists && userData && devPlanID) {
                        let plan_name = `${userData.id}_${devPlanID}`;
                        fetchTimeseriesData('subcatchment', clickedFeature.properties.NAME, 'runoff', selectedStormOption, plan_name);
                        fetchTimeseriesData('subcatchment', clickedFeature.properties.NAME, 'infiltration', selectedStormOption, plan_name);
                    }
                }
            } else {
                setMapClickInfo(prevInfo => ({ ...prevInfo, selectedLayer: 'node' }));
                fetchTimeseriesData('node', clickedFeature.properties.NAME, 'total_inflow', selectedStormOption);
                    if (outFileExists && userData && devPlanID) {
                        let plan_name = `${userData.id}_${devPlanID}`;
                        fetchTimeseriesData('node', clickedFeature.properties.NAME, 'total_inflow', selectedStormOption, plan_name);
                    }
            }
            
        } else {
            setSelectedFeature(prevFeat => ({ ...prevFeat, ID: null }));
            setSelectedFeature(prevFeat => ({ ...prevFeat, Data: null }));
            setLIDType('');
        }
    };

    // Closes the draggable panel when the close button is clicked
    const handleCloseDraggable = () => {
        setSelectedFeature(prevFeat => ({ ...prevFeat, ID: null, Data: null }));
        setTimeSeries({ timeseriesData: null, timeseriesSum: null, rainfallData: null, rainfallSum: null });
    };

    // this doesn't really work the way it should rn ...
    const handleCloseLIDPanel = () => {
        setLIDMode(false);
        setLIDType('');
    };

    // Toggle the visibility of the storm mode panel
    const handleStormMode = () => {
        setStormMode(!stormMode);
    };

    const handleLIDMode = () => {
        setLIDMode(!LIDMode);
    };

    const handleFloodingVisual = () => {
        setFloodingVisual(!floodingVisual);
    }

    const value = {
        geoJSONData,
        setGeoJSONData,
        mapClickInfo,
        setMapClickInfo,

        layersVisible,
        setLayersVisible,
        colorScheme,
        setColorScheme,
        
        timeSeries,
        setTimeSeries,
        selectedFeature,
        setSelectedFeature,
        stormMode,
        setStormMode,
        LIDMode,
        setLIDMode,
        LIDType,
        setLIDType,
        // LIDContent,
        // setLIDContent,

        floodingVisual,
        setFloodingVisual,

        mapRef,

        stormOptions,
        selectedStormOption,
        setSelectedStormOption,

        loading,

        fetchTimeseriesData,
        fetchRainfallData,
        handleMapClick,
        handleCloseDraggable,
        handleStormMode,
        handleCloseLIDPanel,
        handleLIDMode,

        userData,
        setUserData,
        devPlanID,
        setDevPlanID,
        inpFile,
        setInpFile,
        outFileExists,
        setOutFileExists,
        planHasBeenRun,

        systemTotals,
        setSystemTotals,
        systemResults,
        aggregatedCounts,
        setAggregatedCounts,
        aggregateLIDCounts,

        devPlanContent,
        setDevPlanContent,

    }

    return (
        <MapContext.Provider value={value}>
            {children}
        </MapContext.Provider>
    );
};

export const useMapContext = () => {
    const context = useContext(MapContext);
    if (!context) {
        throw new Error("useMapContext must be used within a MapProvider");
    }
    return context;
}
